/**
 * Admin → Deliveries needing follow-up (Phase business-model step 8)
 *
 * Lists deliveries whose T-3 reminder was sent > 12h ago and which the
 * customer has NOT confirmed yet. Admin’s daily call queue.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Phone, PhoneCall, User, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { getToken } from '@/lib/adminApi';

const API = process.env.REACT_APP_BACKEND_URL || '';

async function adminFetch(path, opts = {}) {
  const t = getToken();
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      Authorization: t ? `Bearer ${t}` : '',
      ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
  if (!res.ok) throw new Error((await res.text()) || `${res.status}`);
  return res.status === 204 ? null : res.json();
}

function hoursSince(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60));
  } catch { return null; }
}

export default function AdminFollowUp() {
  const [threshold, setThreshold] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogFor, setDialogFor] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminFetch(`/api/admin/deliveries/needs-followup?threshold_hours=${threshold}`);
      setData(d);
    } catch (e) {
      toast.error(e.message || 'Failed to load');
    } finally { setLoading(false); }
  }, [threshold]);

  useEffect(() => { load(); }, [load]);

  const confirmByPhone = async () => {
    if (!dialogFor) return;
    setBusy(true);
    try {
      await adminFetch(`/api/admin/deliveries/${dialogFor.id}/mark-confirmed-by-phone`, {
        method: 'POST',
        body: JSON.stringify({ note: note.trim() }),
      });
      toast.success('Confirmed — payment message queued.');
      setDialogFor(null);
      setNote('');
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-followup-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[.18em] text-[#F25C05] font-bold">Operations · Follow-up</p>
          <h1 className="font-display text-3xl text-[#05223D]">Needs Follow-up</h1>
          <p className="text-sm text-[#465B70] mt-1 max-w-2xl">Deliveries where the T-3 reminder went out but the customer hasn’t confirmed yet. Call them, then record the confirmation here.</p>
        </div>
      </header>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#465B70] font-bold">Threshold (hours since reminder)</label>
            <Input type="number" min={1} max={168} value={threshold} onChange={(e) => setThreshold(Math.max(1, parseInt(e.target.value || '12', 10)))} className="h-10 rounded-xl mt-1 w-32" data-testid="followup-threshold" />
          </div>
          <Button onClick={load} disabled={loading} className="h-10 rounded-xl bg-[#0A4D8C] hover:bg-[#053B70]" data-testid="followup-refresh">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </CardContent>
      </Card>

      {!data ? (
        <div className="flex items-center gap-2 text-[#465B70]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : data.total === 0 ? (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="flex flex-col items-center justify-center py-16 text-emerald-900">
            <PhoneCall className="h-8 w-8 mb-2" />
            <p className="font-bold">All caught up.</p>
            <p className="text-sm text-emerald-800 mt-1">No unconfirmed reminders older than {data.threshold_hours}h.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-2xl border border-[#0A4D8C1A] bg-white overflow-hidden" data-testid="followup-table">
          <div className="px-4 py-3 border-b border-[#0A4D8C14] bg-amber-50/60 flex items-center gap-2 text-amber-900">
            <AlertCircle size={16} />
            <strong>{data.total}</strong> deliver{data.total === 1 ? 'y' : 'ies'} awaiting confirmation for more than {data.threshold_hours}h.
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EB] text-[10px] uppercase tracking-wider text-[#465B70]">
              <tr>
                <th className="text-left px-4 py-2 font-bold">Customer</th>
                <th className="text-left px-4 py-2 font-bold">Product</th>
                <th className="text-right px-4 py-2 font-bold">Qty</th>
                <th className="text-left px-4 py-2 font-bold">Delivery date</th>
                <th className="text-left px-4 py-2 font-bold">Reminder sent</th>
                <th className="px-4 py-2 font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const hrs = hoursSince(r.reminder_sent_at);
                return (
                  <tr key={r.id} className="border-t border-[#0A4D8C14] hover:bg-amber-50/30" data-testid={`followup-row-${r.id}`}>
                    <td className="px-4 py-2">
                      <div className="font-bold text-[#05223D]">{r.customer_name || '—'}</div>
                      <div className="text-[10px] text-[#7A8A99] flex items-center gap-1"><User size={9} /> {r.customer_phone || 'no phone'}</div>
                    </td>
                    <td className="px-4 py-2">{r.product_name}<div className="text-[10px] text-[#7A8A99]">{r.subscription_label}</div></td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.qty}</td>
                    <td className="px-4 py-2 text-[#465B70]">{(r.scheduled_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-2">
                      <Badge variant="secondary" className="rounded-full bg-amber-100 text-amber-900 text-[10px]">{hrs != null ? `${hrs}h ago` : '—'}</Badge>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" onClick={() => { setDialogFor(r); setNote(''); }} className="h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`followup-mark-${r.id}`}>
                        <Phone className="mr-1.5 h-3 w-3" /> Mark confirmed by phone
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirm-by-phone dialog */}
      <Dialog open={!!dialogFor} onOpenChange={(o) => !o && setDialogFor(null)}>
        <DialogContent data-testid="followup-dialog">
          <DialogHeader>
            <DialogTitle>Mark confirmed by phone</DialogTitle>
            <DialogDescription>Optional note about the call (kept in audit log).</DialogDescription>
          </DialogHeader>
          {dialogFor && (
            <div className="rounded-lg border border-[#0A4D8C1A] bg-[#FDFBF7] p-3 text-sm">
              <div className="font-bold text-[#05223D]">{dialogFor.customer_name}</div>
              <div className="text-[#465B70]">{dialogFor.product_name} × {dialogFor.qty} · {(dialogFor.scheduled_at || '').slice(0, 10)}</div>
            </div>
          )}
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Call notes…" rows={3} data-testid="followup-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogFor(null)} data-testid="followup-cancel">Cancel</Button>
            <Button onClick={confirmByPhone} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="followup-confirm">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PhoneCall className="mr-2 h-4 w-4" />}
              Mark confirmed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
